import React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card } from '@/components/ui/card';
import { gbp, monthLabel, ProfitPill } from '@/components/training/analytics/shared';

export const STREAM_COLOURS = { training: '#0f766e', probound: '#1d4ed8', proepoxy: '#7e22ce', prodecor: '#b45309', material_order: '#475569', sales_log: '#be185d' };
export const BRAND_COLOURS = { probound: '#1d4ed8', proepoxy: '#7e22ce', prodecor: '#b45309', other: '#9ca3af' };

function RankedList({ title, rows, colours, showProfit, testid, emptyText }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <h2 className="font-bold text-gray-900 mb-3">{title}</h2>
      {rows.length === 0 ? <p className="text-sm text-gray-500 italic">{emptyText}</p> : (
        <ol className="space-y-2.5">
          {rows.map((r, i) => (
            <li key={r.key} className="text-sm" data-testid={`${testid}-row-${r.key}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-gray-900 truncate"><span className="text-gray-400 tabular-nums mr-1.5">{i + 1}.</span>{r.label}</span>
                <span className="tabular-nums font-bold text-gray-900">{gbp(r.revenue)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full" style={{ width: `${Math.max(1.5, r.share)}%`, background: colours[r.key] || '#9ca3af' }} /></div>
                <span className="text-[11px] text-gray-500 tabular-nums w-40 text-right">{r.share}% · {r.count} {r.count === 1 ? 'sale' : 'sales'}</span>
              </div>
              {showProfit && (
                <div className="text-[11px] text-gray-500 mt-0.5 flex gap-3">
                  <span>Cost {gbp(r.cost)}</span>
                  <span>Profit <ProfitPill value={r.profit} /></span>
                  {r.margin_pct !== null && r.margin_pct !== undefined && <span>Margin {r.margin_pct}%</span>}
                  {r.uncosted > 0 && <span className="text-amber-700">{r.uncosted} without costs</span>}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export function StreamsCard({ data }) {
  return <RankedList title="Revenue by part of the business" rows={data.streams.filter((s) => s.revenue > 0 || s.count > 0)} colours={STREAM_COLOURS} showProfit={data.can_view_profit} testid="streams-card" emptyText="No revenue in this window." />;
}

export function BrandsCard({ data }) {
  return <RankedList title="Revenue by brand" rows={data.brands} colours={BRAND_COLOURS} showProfit={data.can_view_profit} testid="brands-card" emptyText="No revenue in this window." />;
}

export function MonthlyChart({ data }) {
  const streams = data.filters.all_streams.filter((s) => data.filters.streams.includes(s.key));
  const rows = data.months.map((m) => ({ name: monthLabel(m.month), ...Object.fromEntries(streams.map((s) => [s.label, m.by_stream[s.key] || 0])) }));
  return (
    <Card className="p-4" data-testid="overview-chart">
      <h2 className="font-bold text-gray-900 mb-1">Monthly revenue trend</h2>
      <p className="text-[11px] text-gray-500 mb-3">Stacked by part of the business · all figures ex VAT.</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} width={48} />
            <Tooltip formatter={(v) => gbp(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {streams.map((s, i) => <Bar key={s.key} dataKey={s.label} stackId="rev" fill={STREAM_COLOURS[s.key]} radius={i === streams.length - 1 ? [3, 3, 0, 0] : 0} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export function TopProducts({ rows }) {
  const max = rows[0]?.revenue || 1;
  return (
    <Card className="p-0 overflow-hidden" data-testid="top-products">
      <div className="px-4 py-3 border-b bg-gray-50"><h2 className="font-bold text-gray-900">Top products by revenue</h2><p className="text-[11px] text-gray-500">From order line items · courses count as products for training</p></div>
      {rows.length === 0 ? <p className="px-4 py-6 text-sm text-gray-500 italic">Nothing sold in this window.</p> : (
        <ol className="divide-y">
          {rows.map((p, i) => (
            <li key={`${p.stream}-${p.name}`} className="px-4 py-2 text-sm" data-testid={`top-product-${i}`}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate"><span className="text-gray-400 tabular-nums mr-1.5">{i + 1}.</span><span className="font-semibold text-gray-900">{p.name}</span> <span className="text-[10px] uppercase tracking-wide font-bold" style={{ color: STREAM_COLOURS[p.stream] }}>{p.stream_label}</span></span>
                <span className="tabular-nums font-bold text-gray-900 shrink-0">{gbp(p.revenue)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full" style={{ width: `${Math.max(1.5, (p.revenue / max) * 100)}%`, background: STREAM_COLOURS[p.stream] }} /></div>
                <span className="text-[11px] text-gray-500 tabular-nums w-32 text-right">qty {p.qty} · {p.orders} order{p.orders === 1 ? '' : 's'}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
